import { Vec3 } from '../engine/math/vec3';
import { AABB } from '../engine/math/aabb';
import { clamp, damp, easeInCubic, lerp } from '../engine/math/easing';
import { Rng } from '../engine/math/random';
import type { Heightfield } from '../engine/geo/heightfield';
import type { FloorPatch } from '../engine/physics/StaticWorld';
import type { RGB } from '../engine/render/Renderer';
import type { Ctx } from './Ctx';
import { Player, PLAYER } from './player/Player';
import { Combat } from './player/Combat';
import type { ThirdPersonCamera } from './camera/ThirdPersonCamera';
import { Items, ItemState } from './items/Items';
import { Keep, KEEP_DEFS, buildKeep, updateKeeps } from './world/Keeps';
import { BiomeId, ATMOS, Atmosphere, SPOTS, biomeAt } from './world/Layout';
import { buildTerrain, MARSH_DEADLY_Y, MARSH_WATER_Y } from './world/Terrain';
import { buildHearth } from './biomes/Hearth';
import { buildMarsh, MarshHandles } from './biomes/Marsh';
import { buildDunes, DunesHandles } from './biomes/Dunes';
import { buildIronwood, IronwoodHandles } from './biomes/Ironwood';
import { buildCistern, CisternHandles } from './biomes/Cistern';
import { Beast } from './beasts/Beast';
import { BogEel } from './beasts/BogEel';
import { Scarab } from './beasts/Scarab';
import { Stag } from './beasts/Stag';
import { Wyrm } from './beasts/Wyrm';
import { Magpie } from './beasts/Magpie';
import { Particles } from './fx/Particles';
import { Hud } from './ui/Hud';
import { shuffle as drawShuffle, Assignment } from './world/Shuffle';
import { buildSecretRoom, updateSecretRoom, SecretRoom } from './world/Secret';
import { writeSave, applySave, SaveData } from './world/Save';
import type { ItemId } from './items/ItemDefs';
import type { ItemLoc } from './items/Items';

export type Mode = 'A' | 'B';

/** A crossable channel/chasm the Plank snaps into (§4). */
export interface GapMarker { id: string; pos: Vec3; dir: Vec3; floor: FloorPatch; biome: BiomeId }

const LANTERN_RADIUS = 14;

/** Owns everything in a run: terrain, biomes, items, keeps, beasts, the player, and the rules that tie them together. */
export class World {
  hfs!: Record<BiomeId, Heightfield>;
  player!: Player;
  combat!: Combat;
  items!: Items;
  keeps: Keep[] = [];
  beasts: Beast[] = [];
  magpie!: Magpie;
  gaps: GapMarker[] = [];
  particles!: Particles;
  hud: Hud;
  biome: BiomeId = 'hearth';
  deaths = 0;
  startTime = 0;
  won = false;
  foundSecret = false;
  /** Mode B item placement (spot ids) or null in Mode A. */
  shuffle: Record<string, string> | null = null;
  /** Elapsed seconds carried over from a loaded save. */
  timeOffset = 0;
  secret!: SecretRoom;
  maze!: IronwoodHandles;
  private marsh!: MarshHandles;
  private dunes!: DunesHandles;
  private cistern!: CisternHandles;
  private atmos: Atmosphere;
  private healT = 0;
  private dying = false;
  private lastNear: string | null = null;
  private boomTarget = 3.2;
  private placedPlank: GapMarker | null = null;
  private readonly rng = new Rng(7);
  private nextLightning = 6;
  private lightning = 0;
  private inWater = false;
  private readonly lastDry = new Vec3();
  private readonly dryHistory: Vec3[] = [];
  private dryT = 0;
  private transitionCd = 0;
  private lanternLit = false;
  private dark = 1; // cistern darkness blend
  onWin?: (stats: { time: number; deaths: number; secret: boolean }) => void;

  constructor(private readonly ctx: Ctx, private readonly tpc: ThirdPersonCamera, uiRoot: HTMLElement, readonly mode: Mode) {
    this.hud = new Hud(uiRoot);
    this.atmos = cloneAtmos(ATMOS.hearth);
  }

  get elapsed(): number { return this.ctx.time - this.startTime + this.timeOffset; }

  build(save: SaveData | null = null): void {
    const ctx = this.ctx;
    this.hfs = buildTerrain(ctx);
    buildHearth(ctx);
    this.marsh = buildMarsh(ctx);
    this.dunes = buildDunes(ctx);
    this.maze = buildIronwood(ctx);
    this.cistern = buildCistern(ctx);
    const gold = buildKeep(ctx, { ...KEEP_DEFS.gold, pos: SPOTS.goldKeep.clone() });
    const white = buildKeep(ctx, { ...KEEP_DEFS.white, pos: SPOTS.whiteKeep.clone() });
    const black = buildKeep(ctx, { ...KEEP_DEFS.black, pos: SPOTS.blackKeep.clone() });
    this.keeps.push(gold, white, black);

    // gap markers (§4): marsh neck, cistern chasm, ironwood ravine shortcut
    this.addGap('marsh.neck', new Vec3(-218, MARSH_WATER_Y + 0.08, -4), new Vec3(-6, 0, 8).normalize(), 'marsh');
    this.addGap('cistern.chasm', new Vec3(SPOTS.cisternChasm.x, -0.05, 560), new Vec3(1, 0, 0), 'cistern');
    this.addGap('ironwood.ravine', new Vec3(170, ctx.statics.terrainHeight(170, -8) + 0.1, -5), new Vec3(0, 0, 1), 'ironwood');

    const spawn = SPOTS.spawn.clone();
    spawn.y = ctx.statics.terrainHeight(spawn.x, spawn.z);
    this.player = new Player(ctx, this.tpc, spawn);
    this.player.facing = Math.PI;
    this.player.root.rotation.setYaw(Math.PI);
    this.tpc.yaw = Math.PI;
    this.player.snapAll();
    this.combat = new Combat(ctx, this.player);
    this.combat.onHitbox = (c, r, hitSet) => this.resolveAttack(c, r, hitSet);
    this.particles = new Particles(ctx);
    this.items = new Items(ctx, this.player);

    // items: Mode A fixed layout (§6) or Mode B shuffle (§6.1)
    const shrineY = ctx.statics.terrainHeight(SPOTS.marshShrine.x, SPOTS.marshShrine.z) + 1.4;
    const spots: Record<string, { pos: Vec3; loc?: ItemLoc }> = {
      marshNearIsland: { pos: SPOTS.marshNearIsland.clone() }, hearthHedges: { pos: new Vec3(-8, 0, 40) }, ironwoodLog: { pos: this.maze.cellCenter(1, 1).clone().add(new Vec3(0, 0, 1.8)) }, dunesRidge: { pos: new Vec3(30, 0, -180) },
      goldDais: { pos: gold.dais.clone() }, hearthStream: { pos: new Vec3(-36, 0, 6) }, marshShore: { pos: new Vec3(-152, 0, 6) }, ironwoodArch: { pos: this.maze.cellCenter(2, 7).clone().add(new Vec3(0, 0, 2)) },
      marshShrine: { pos: new Vec3(SPOTS.marshShrine.x, shrineY, SPOTS.marshShrine.z) }, dunesBuried: { pos: SPOTS.whiteKeyBuried.clone(), loc: 'buried' }, ironwoodRing: { pos: this.maze.cellCenter(7, 2).clone() },
      cisternLedge: { pos: new Vec3(-30, 0, 560) }, marshSmallIsland: { pos: new Vec3(-170, 0, -30) }, dunesCage: { pos: this.dunes.cageCenter.clone(), loc: 'caged' }, whiteDais: { pos: white.dais.clone() },
      cisternFarLedge: { pos: new Vec3(48, 0, 600) }, hearthSignpost: { pos: new Vec3(6, 0, 17) }, dunesObelisk: { pos: new Vec3(SPOTS.obelisk.x + 2, 0, SPOTS.obelisk.z) },
    };
    this.items.spawn('sword', SPOTS.swordSpot.clone());
    this.items.spawn('chalice', black.dais.clone());
    this.items.spawn('greyDot', SPOTS.greyDotNiche.clone());
    let magpieHolds: ItemId | null = null;
    if (this.mode === 'B') {
      const rec: Record<string, string> = save?.shuffle ?? (() => { const d = drawShuffle((Date.now() & 0xffff) + 1); return { ...(d.assignment as unknown as Record<string, string>), _magpie: d.magpieHolds }; })();
      this.shuffle = rec;
      magpieHolds = (rec._magpie as ItemId) ?? null;
      const shuffled: (keyof Assignment)[] = ['plank', 'magnet', 'goldKey', 'whiteKey', 'blackKey', 'lantern'];
      for (const id of shuffled) {
        const sp = spots[rec[id]];
        this.items.spawn(id, sp.pos.clone(), sp.loc ?? 'ground');
      }
    } else {
      this.items.spawn('magnet', gold.dais.clone());
      this.items.spawn('plank', SPOTS.marshNearIsland.clone());
      this.items.spawn('goldKey', spots.marshShrine.pos.clone());
      this.items.spawn('whiteKey', SPOTS.whiteKeyBuried.clone(), 'buried');
      this.items.spawn('blackKey', this.dunes.cageCenter.clone(), 'caged');
      this.items.spawn('lantern', white.dais.clone());
    }

    // beasts (§5)
    this.beasts.push(new BogEel(ctx, new Vec3(-208, 0, -6)));
    this.beasts.push(new Scarab(ctx, new Vec3(-18, 0, -196)));
    const stag = new Stag(ctx, this.maze.cellCenter(1, 4), this.maze);
    stag.particles = this.particles;
    this.beasts.push(stag);
    this.beasts.push(new Wyrm(ctx, SPOTS.wyrmHome.clone()));
    this.magpie = new Magpie(ctx, new Vec3(30, 0, -30));
    this.magpie.noDropZones = this.keeps.map((k) => k.interior);
    if (magpieHolds && !save) { const it = this.items.get(magpieHolds); if (it) this.magpie.grabNow(it, this.items); }
    this.secret = buildSecretRoom(ctx, gold);

    this.items.onDrop = (it) => this.particles.puff(it.t.position, 6);
    this.items.onBounce = (it) => this.particles.puff(it.t.position, 4);
    this.items.onPickup = (it) => this.onPickup(it);
    this.player.onDodge = () => ctx.sfx.dodge();
    this.player.onLand = (i) => { ctx.sfx.land(i); if (i > 0.3) this.particles.puff(this.player.position, 6); };
    this.player.onStep = () => {
      const s = this.surfaceAt(this.player.position);
      ctx.sfx.step(s, ctx.input.isDown('ShiftLeft'));
      if (s === 'water') this.particles.splash(_v.set(this.player.position.x, MARSH_WATER_Y, this.player.position.z), 5);
    };

    this.applyAtmos(this.atmos);
    this.tpc.snapTo(this.player.position);
    this.lastDry.copy(this.player.position);
    this.dryHistory.push(this.player.position.clone());
    this.startTime = ctx.time;
    if (save) {
      applySave(this, save);
      this.timeOffset = save.time;
      this.lastDry.copy(this.player.position);
      const b = biomeAt(this.player.position.x, this.player.position.z);
      this.biome = b;
      this.atmos = cloneAtmos(ATMOS[b]);
      this.applyAtmos(this.atmos);
      this.tpc.snapTo(this.player.position);
    }
    ctx.sfx.setBiomeAmbient(this.biome, 0.1);
  }

  /** Open a gate instantly (save restore). */
  forceOpen(k: Keep): void {
    k.open = true; k.opening = false; k.gateCollider.enabled = false;
    k.bars.position.y = k.gatePos.y + 4.05;
    k.bars.snapPrev();
  }
  /** Snap the plank into a gap without the fanfare (save restore). */
  placePlankAt(it: ItemState, g: GapMarker): void {
    this.items.release(it, g.pos.clone());
    it.loc = 'placed'; it.placedAt = g.id; it.sleeping = true;
    it.t.position.copy(g.pos); it.t.rotation.setYaw(Math.atan2(-g.dir.z, g.dir.x)); it.t.snapPrev();
    g.floor.enabled = true;
    this.placedPlank = g;
    if (g.id === 'ironwood.ravine') this.maze.blocked.delete('1,3-1,4');
  }
  save(): void { writeSave(this, this.elapsed); }

  private addGap(id: string, pos: Vec3, dir: Vec3, biome: BiomeId): GapMarker {
    const ex = Math.abs(dir.x) * 2.5 + Math.abs(dir.z) * 0.9, ez = Math.abs(dir.z) * 2.5 + Math.abs(dir.x) * 0.9;
    const floor = this.ctx.statics.addFloor(AABB.fromMinMax(pos.x - ex, pos.y - 1, pos.z - ez, pos.x + ex, pos.y, pos.z + ez), 'plank');
    floor.enabled = false;
    const g = { id, pos, dir, floor, biome };
    this.gaps.push(g);
    return g;
  }

  private onPickup(it: ItemState): void {
    if (it.def.id === 'plank' && this.placedPlank) { if (this.placedPlank.id === 'ironwood.ravine') this.maze.blocked.add('1,3-1,4'); this.placedPlank.floor.enabled = false; this.placedPlank = null; }
    if (it.def.id === 'chalice') {
      // §10: everything else desaturates for 0.4 s, a sustained chord, the camera slowly dollies out
      const ctx = this.ctx;
      ctx.sfx.chaliceGrab();
      ctx.tweens.add(0.4, (k) => { ctx.frame.post.desat = 1 - k; });
      const base = this.tpc.boomLength;
      ctx.tweens.add(1.5, (k) => { this.boomTarget = base + Math.sin(k * Math.PI) * 1.2; });
      ctx.fx.fovKick(-4);
    }
  }

  surfaceAt(p: Vec3): 'grass' | 'sand' | 'water' | 'stone' | 'wood' {
    const g = this.ctx.statics.ground(p.x, p.y + 0.2, p.z, 0.5);
    if (g.floor?.tag === 'plank') return 'wood';
    for (const k of this.keeps) if (k.interior.containsPoint(p)) return 'stone';
    switch (this.biome) {
      case 'dunes': return 'sand';
      case 'marsh': return this.inWater ? 'water' : 'grass';
      case 'cistern': return this.inWater ? 'water' : 'stone';
      default: return 'grass';
    }
  }

  get insideKeep(): Keep | null {
    for (const k of this.keeps) if (k.interior.containsPoint(this.player.position)) return k;
    return null;
  }

  update(dt: number): void {
    const ctx = this.ctx, input = ctx.input, pl = this.player;
    if (this.won) { this.items.update(dt); this.particles.update(dt); this.hud.update(dt); return; }
    const b = biomeAt(pl.position.x, pl.position.z);
    if (b !== this.biome) this.enterBiome(b);

    const alive = !this.dying && pl.state !== 'frozen';
    if (alive) {
      if (input.wasPressed('KeyE')) this.items.interact();
      if (input.wasPressed('KeyF')) this.useHeld();
    }
    this.combat.update(dt, this.items.isHolding('sword') && alive);
    this.items.update(dt);
    updateKeeps(ctx, this.keeps, this.items, dt, () => { this.tpc.boomLength += 0.5; });

    // lights: a lit lantern anywhere it can be seen, else the chalice, else (in the cistern) a dim body glow
    const lantern = this.items.get('lantern')!;
    this.lanternLit = lantern.lit && (lantern.loc === 'held' || lantern.loc === 'ground');
    const lanternPos = this.lanternLit ? this.items.worldPos(lantern, _light).clone() : null;
    const chalice = this.items.get('chalice')!;
    const chalicePos = chalice.loc === 'held' || chalice.loc === 'ground' ? this.items.worldPos(chalice, _light2).clone() : null;
    const inCistern = this.biome === 'cistern';
    if (lanternPos) {
      lanternPos.y += lantern.loc === 'held' ? 0.2 : 0.4;
      ctx.frame.pointLight = { pos: lanternPos, color: [2.4, 1.9, 1.1], radius: LANTERN_RADIUS, shadows: inCistern };
    } else if (chalicePos && chalicePos.distanceTo(pl.position) < 40) {
      chalicePos.y += 0.5;
      ctx.frame.pointLight = { pos: chalicePos, color: [1.4, 1.1, 0.5], radius: 8, shadows: false };
    } else if (inCistern) {
      ctx.frame.pointLight = { pos: _light.copy(pl.position).add(_v.set(0, 1.4, 0)).clone(), color: [0.14, 0.18, 0.32], radius: 3.5, shadows: false };
    } else ctx.frame.pointLight = null;
    this.dark = damp(this.dark, inCistern && !lanternPos ? 1 : 0, 0.35, dt);

    // beasts (only the nearby ones tick)
    const wyrm = this.beasts.find((x) => x.kind === 'wyrm') as Wyrm | undefined;
    if (wyrm) wyrm.light = lanternPos ? { pos: lanternPos, radius: LANTERN_RADIUS } : null;
    for (const be of this.beasts) if (be.alive && be.position.distanceTo(pl.position) < 130) be.update(dt, pl);
    this.magpie.update(dt, pl, this.items, this.particles);

    // magnet (§4): hum near metal, pulse on F (handled in useHeld)
    if (this.items.isHolding('magnet')) {
      let level = 0;
      for (const it of this.items.metalNear(pl.position, 12)) {
        const d = (it.loc === 'magpie' ? it.t.getWorldPosition(_v) : it.t.position).distanceTo(pl.position);
        level = Math.max(level, 1 - d / 12);
      }
      ctx.sfx.magnetHum(level * level);
    } else ctx.sfx.magnetHum(0);

    // water / pit rules
    const g = ctx.statics.ground(pl.position.x, pl.position.y + 0.2, pl.position.z, 0.5);
    const th = ctx.statics.terrainHeight(pl.position.x, pl.position.z);
    const onFloor = !!g.floor;
    const wet = this.biome === 'marsh' || this.biome === 'cistern';
    this.inWater = !onFloor && wet && th < MARSH_WATER_Y - 0.05 && pl.grounded;
    if (pl.grounded && !onFloor && (!wet || th > MARSH_WATER_Y + 0.05) && th > -2.5 && alive) {
      this.dryT += dt;
      if (this.dryT >= 1) {
        this.dryT = 0;
        this.dryHistory.push(pl.position.clone());
        if (this.dryHistory.length > 3) this.dryHistory.shift();
        this.lastDry.copy(this.dryHistory[0]); // the oldest sample: a few metres back from the edge
      }
    }
    pl.speedMul = this.inWater ? 0.6 : 1;
    if (alive && !onFloor && wet && th < MARSH_DEADLY_Y && pl.grounded) this.die('sink');
    if (alive && (this.biome === 'cistern' || this.biome === 'ironwood') && pl.position.y < -3.5) this.die('sink');

    // keep interior: shorter boom
    const inside = this.insideKeep;
    this.boomTarget = inside ? 2.2 : 3.2;
    this.tpc.boomLength = damp(this.tpc.boomLength, this.boomTarget, 0.25, dt);
    this.tpc.lateralExtra = damp(this.tpc.lateralExtra, this.items.isHolding('lantern') ? -0.15 : 0, 0.2, dt);

    // stone circle heal (§3.4)
    const sc = SPOTS.stoneCircle;
    if (pl.position.distanceToXZ(sc) < 5.5 && pl.hearts < PLAYER.maxHearts && pl.state !== 'dead') {
      this.healT += dt;
      if (this.healT >= 2) { pl.hearts = PLAYER.maxHearts; ctx.sfx.heal(); this.healT = 0; this.particles.burst(_v.copy(pl.position).add(_up), 14, [1, 0.85, 0.45], 2.5, 0.12, 0.9, 0.8); }
    } else this.healT = 0;

    pl.runMulOverride = this.items.isHolding('chalice') ? 1.3 : null;
    if (pl.hearts <= 0 && !this.dying) this.die('beast');

    // well ↔ ladder transitions (arrival points sit outside the opposite trigger; a short cooldown covers the fade)
    this.transitionCd = Math.max(0, this.transitionCd - dt);
    if (alive && this.transitionCd <= 0) {
      // you have to walk into the well / ladder (or stand at it), not merely pass by
      const into = (c: Vec3, r: number): boolean => {
        if (pl.position.distanceToXZ(c) > r) return false;
        const vx = pl.velocity.x, vz = pl.velocity.z, sp = Math.hypot(vx, vz);
        if (sp < 0.4) return true;
        const dx = c.x - pl.position.x, dz = c.z - pl.position.z, d = Math.hypot(dx, dz) || 1;
        return (vx * dx + vz * dz) / (sp * d) > 0.5;
      };
      if (this.biome === 'ironwood' && into(SPOTS.well, 2.3)) this.transition(new Vec3(SPOTS.cisternEntry.x, 0, SPOTS.cisternEntry.z + 1), Math.PI / 2);
      else if (this.biome === 'cistern' && into(this.cistern.ladderPos, 1.7)) this.transition(new Vec3(SPOTS.well.x, 0, SPOTS.well.z + 3.2), 0);
    }

    // win (§2.1): the Chalice inside the Gold Keep
    if (alive && chalicePos && this.keeps[0].interior.containsPoint(chalicePos)) this.win();

    // secret room (§9): the Grey Dot opens the east wall
    const dot = this.items.get('greyDot')!;
    const dotPos = dot.loc === 'held' || dot.loc === 'ground' ? this.items.worldPos(dot, _v2) : null;
    if (updateSecretRoom(this.secret, dotPos, pl.position, ctx.time, dt)) { this.foundSecret = true; ctx.sfx.secret(); ctx.fx.fovKick(-5); }

    // biome effects
    if (this.biome === 'marsh') {
      this.nextLightning -= dt;
      if (this.nextLightning <= 0) {
        this.nextLightning = this.rng.range(9, 22);
        this.lightning = 1;
        ctx.tweens.after(this.rng.range(1.2, 2.6), () => ctx.sfx.thunder());
      }
      this.marsh.update(pl.position, dt);
    }
    if (this.lightning > 0) {
      ctx.frame.post.white = this.lightning * 0.28;
      this.lightning = Math.max(0, this.lightning - dt * 7);
      if (this.lightning === 0) ctx.frame.post.white = 0;
    }
    if (this.biome === 'dunes') this.dunes.update(pl.position, this.particles, dt);
    if (this.biome === 'ironwood') this.maze.update(pl.position, this.particles, dt);
    if (this.biome === 'cistern') this.cistern.update(pl.position, this.particles, dt);
    if (this.biome === 'hearth' && this.rng.chance(dt * 1.5)) {
      // dandelion seeds
      const x = pl.position.x + this.rng.range(-14, 14), z = pl.position.z + this.rng.range(-14, 14);
      this.particles.mote(_v.set(x, ctx.statics.terrainHeight(x, z) + this.rng.range(0.5, 2.5), z), _v2.set(0.6, 0.15, 0.2), 4, 0.06, [1, 1, 0.95], 0.3, 0);
    }

    // atmosphere crossfade (+ cistern darkness override)
    blendAtmos(this.atmos, ATMOS[this.biome], dt, 2.2);
    this.applyAtmos(this.atmos);

    // HUD
    this.hud.hearts(pl.hearts, PLAYER.maxHearts);
    this.hud.held(this.items.held?.def.id ?? null);
    const near = this.items.nearest();
    const nearId = near ? near.def.id : null;
    if (nearId !== this.lastNear) { this.lastNear = nearId; this.hud.hint(near ? (this.items.held ? 'E · swap for ' + near.def.name : 'E · take ' + near.def.name) : null, 3); }
    this.hud.update(dt);
    this.particles.update(dt);
  }

  private transition(to: Vec3, facing: number): void {
    const ctx = this.ctx, pl = this.player;
    this.transitionCd = 2.5;
    pl.state = 'frozen';
    ctx.sfx.step('stone');
    ctx.tweens.add(0.45, (k) => { ctx.frame.post.fade = k; }, undefined, () => {
      to.y = ctx.statics.ground(to.x, ctx.statics.terrainHeight(to.x, to.z) + 0.5, to.z, 1.0).y;
      pl.teleport(to, facing);
      this.dryHistory.length = 0; this.dryHistory.push(to.clone()); this.lastDry.copy(to);
      this.tpc.yaw = facing;
      this.tpc.snapTo(pl.position);
      const b = biomeAt(to.x, to.z);
      if (b !== this.biome) this.enterBiome(b);
      // snap the atmosphere so we don't crossfade daylight into the dark
      this.atmos = cloneAtmos(ATMOS[b]);
      this.applyAtmos(this.atmos);
      ctx.tweens.add(0.5, (k) => { ctx.frame.post.fade = 1 - k; }, undefined, () => { pl.state = 'idle'; }, 0.2);
    });
  }

  /** Sword hitbox vs beasts and the magpie. */
  private resolveAttack(center: Vec3, r: number, hitSet: Set<object>): void {
    for (const be of this.beasts) {
      if (!be.alive || hitSet.has(be)) continue;
      if (be.hitTest(center, r)) {
        hitSet.add(be);
        if (be.hurt(this.player.position, this.particles)) { this.player.squash.kick(2); this.ctx.fx.shake(0.12, 0.15); }
      }
    }
    if (!hitSet.has(this.magpie) && this.magpie.position.distanceTo(center) < r + 0.6) {
      hitSet.add(this.magpie);
      this.magpie.hit(this.player.position, this.items, this.particles);
      this.ctx.fx.hitStop(0.05);
    }
  }

  private nearestGap(): GapMarker | null {
    const p = this.player.position;
    let best: GapMarker | null = null, bd = 5.5;
    for (const g of this.gaps) {
      if (g.floor.enabled) continue;
      const d = g.pos.distanceToXZ(p);
      if (d < bd) {
        const to = _v.copy(g.pos).sub(p); to.y = 0; to.normalize();
        const f = _v2.set(Math.sin(this.player.facing), 0, Math.cos(this.player.facing));
        if (to.dot(f) > 0.2) { best = g; bd = d; }
      }
    }
    return best;
  }

  private placePlank(it: ItemState, g: GapMarker): void {
    this.placePlankAt(it, g);
    this.ctx.sfx.plankPlace(g.pos);
    this.ctx.fx.shake(0.1, 0.2);
    this.particles.splash(g.pos, 12, g.biome === 'marsh' ? [0.5, 0.75, 0.7] : [0.6, 0.6, 0.65]);
    this.player.squash.squash(0.85);
  }

  private magnetPulse(): void {
    const ctx = this.ctx, pl = this.player;
    ctx.sfx.magnetPulse();
    ctx.fx.fovKick(3);
    pl.squash.squash(0.85);
    this.particles.burst(_v.copy(pl.position).add(_up), 12, [0.9, 0.2, 0.15], 4, 0.1, 0.5, 0.6);
    for (const it of this.items.metalNear(pl.position, 8)) {
      const kind = this.items.pull(it, pl.position);
      if (kind === 'magpie') { this.magpie.dropLoot(this.items); continue; }
      if (kind === 'erupt') { ctx.sfx.bury(it.t.position); this.particles.burst(it.t.position, 16, [0.95, 0.88, 0.65], 4, 0.16, 0.9, 0); ctx.fx.shake(0.15, 0.3); }
      else if (kind === 'uncage') { ctx.sfx.hit(it.t.position); this.particles.puff(it.t.position, 6, [0.5, 0.5, 0.5]); }
      else this.particles.puff(it.t.position, 4);
    }
  }

  private useHeld(): void {
    const it = this.items.held;
    if (!it) { this.ctx.sfx.shake(); return; }
    switch (it.def.id) {
      case 'lantern':
        it.lit = !it.lit;
        it.rd.emissiveMul = it.lit ? 1 : 0.15;
        this.ctx.sfx.lantern(it.lit);
        break;
      case 'plank': {
        const g = this.nearestGap();
        if (g) this.placePlank(it, g);
        else { this.ctx.sfx.shake(); this.player.squash.kick(-1.5); }
        break;
      }
      case 'magnet':
        this.magnetPulse();
        break;
      default:
        this.ctx.sfx.shake();
        this.player.squash.kick(-1.5);
        break;
    }
  }

  private win(): void {
    if (this.won) return;
    this.won = true;
    const ctx = this.ctx;
    this.player.state = 'frozen';
    ctx.sfx.win();
    ctx.sfx.magnetHum(0);
    ctx.tweens.add(2.0, (k) => { this.tpc.boomLength = 2.2 + k * 2.5; ctx.frame.post.white = Math.sin(k * Math.PI) * 0.25; });
    for (let i = 0; i < 6; i++) ctx.tweens.after(i * 0.25, () => this.particles.burst(_v.copy(this.player.position).add(_up), 18, [1, 0.85, 0.35], 4, 0.14, 1.2, 0.8));
    const stats = { time: this.elapsed, deaths: this.deaths, secret: this.foundSecret };
    ctx.tweens.after(2.2, () => this.onWin?.(stats));
  }

  enterBiome(b: BiomeId): void {
    this.biome = b;
    this.ctx.sfx.setBiomeAmbient(b, 2.5);
    this.nextLightning = 4;
    if (this.player) this.save();
  }

  private applyAtmos(a: Atmosphere): void {
    const f = this.ctx.frame;
    f.sunDir.copy(a.sunDir);
    f.sunColor = a.sunColor; f.skyColor = a.skyColor; f.groundColor = a.groundColor; f.fogColor = a.fogColor;
    f.fogNear = a.fogNear; f.fogFar = a.fogFar; f.skyTop = a.skyTop; f.skyHorizon = a.skyHorizon; f.skyBottom = a.skyBottom;
    f.sunDisc = a.sunDisc; f.shadowsEnabled = a.shadows;
    f.post.vignette = a.vignette; f.post.heat = a.heat; f.post.outline = a.outline;
    if (this.dark > 0.001) {
      // without the lantern the cistern closes in to a 3 m radius of dim blue
      f.fogNear = lerp(f.fogNear, 0.8, this.dark);
      f.fogFar = lerp(f.fogFar, 5.5, this.dark);
      f.post.vignette = lerp(f.post.vignette, 1.6, this.dark);
    }
  }

  /** §3.4: deflate, black, respawn at the circle; the held item drops where you died. */
  die(cause: 'beast' | 'sink'): void {
    if (this.dying) return;
    this.dying = true;
    this.deaths++;
    const pl = this.player, ctx = this.ctx;
    pl.state = 'dead';
    pl.velocity.set(0, 0, 0);
    const deathPos = pl.position.clone();
    if (cause === 'sink') {
      const bank = this.lastDry.clone();
      const it = this.items.held;
      if (it) this.items.release(it, bank.add(_up));
    } else this.items.dropAt(deathPos);
    if (cause === 'sink') ctx.sfx.sink(); else ctx.sfx.die();
    const startY = pl.position.y;
    ctx.tweens.add(cause === 'sink' ? 1.0 : 0.6, (k) => {
      const s = 1 - k;
      if (cause === 'sink') { pl.root.position.y = startY - k * 2.2; pl.body.scale.set(1, 1, 1); }
      else pl.body.scale.set(1 + k * 0.4, s * s, 1 + k * 0.4);
    }, easeInCubic, () => {
      ctx.tweens.add(0.5, (k) => { ctx.frame.post.fade = k; }, undefined, () => {
        const sc = cause === 'sink' ? this.lastDry.clone() : SPOTS.stoneCircle.clone();
        sc.y = ctx.statics.ground(sc.x, sc.y + 2, sc.z, 3).y;
        pl.teleport(sc, 0);
        this.dryHistory.length = 0; this.dryHistory.push(sc.clone()); this.lastDry.copy(sc);
        pl.hearts = PLAYER.maxHearts;
        pl.body.scale.set(1, 1, 1);
        pl.squash.reset();
        this.tpc.snapTo(pl.position);
        const b = biomeAt(sc.x, sc.z);
        if (b !== this.biome) { this.enterBiome(b); this.atmos = cloneAtmos(ATMOS[b]); }
        ctx.tweens.add(0.6, (k) => { ctx.frame.post.fade = 1 - k; }, undefined, () => {
          pl.state = 'idle';
          this.dying = false;
          ctx.sfx.respawn();
        }, 0.5);
      });
    });
  }
}

function cloneAtmos(a: Atmosphere): Atmosphere {
  return { ...a, sunDir: a.sunDir.clone(), sunColor: [...a.sunColor] as RGB, skyColor: [...a.skyColor] as RGB, groundColor: [...a.groundColor] as RGB, fogColor: [...a.fogColor] as RGB, skyTop: [...a.skyTop] as RGB, skyHorizon: [...a.skyHorizon] as RGB, skyBottom: [...a.skyBottom] as RGB };
}
function lerpRGB(a: RGB, b: RGB, k: number): void { a[0] += (b[0] - a[0]) * k; a[1] += (b[1] - a[1]) * k; a[2] += (b[2] - a[2]) * k; }
function blendAtmos(a: Atmosphere, t: Atmosphere, dt: number, seconds: number): void {
  const k = clamp(1 - Math.pow(0.5, dt / (seconds / 4)), 0, 1);
  a.sunDir.lerp(t.sunDir, k).normalize();
  lerpRGB(a.sunColor, t.sunColor, k); lerpRGB(a.skyColor, t.skyColor, k); lerpRGB(a.groundColor, t.groundColor, k); lerpRGB(a.fogColor, t.fogColor, k);
  lerpRGB(a.skyTop, t.skyTop, k); lerpRGB(a.skyHorizon, t.skyHorizon, k); lerpRGB(a.skyBottom, t.skyBottom, k);
  a.fogNear += (t.fogNear - a.fogNear) * k; a.fogFar += (t.fogFar - a.fogFar) * k; a.sunDisc += (t.sunDisc - a.sunDisc) * k;
  a.vignette += (t.vignette - a.vignette) * k; a.heat += (t.heat - a.heat) * k; a.outline += (t.outline - a.outline) * k;
  a.shadows = t.shadows; a.ambientMul += (t.ambientMul - a.ambientMul) * k;
}
const _v = new Vec3(), _v2 = new Vec3(), _up = new Vec3(0, 1, 0), _light = new Vec3(), _light2 = new Vec3();
